import HttpService from '@src/utility/httpService';
import { checkIsInternal } from '@src/utility/createStorage';
import { getContractIdValuesFromSession } from '@src/CommonComponents/ContractIdFilter/contractIdFilter';
import { config } from '@uhg-abyss/web/tools/config';
import {
  CROFilters,
  CRODetailApiResponse,
  CRODetailRequest,
  CRODetailResult,
  CROSummaryApiMetric,
  CROSummaryApiResponse,
  CROSummaryRequest,
  CROSummaryResult,
  SummaryMetric,
  TopMarketInitiativeRow,
} from './types';
import { getConfiguredMetric, getConfiguredTrend, getSummaryMetricOrder } from './croMetricConfig';

export const CRO_SUMMARY_API_ENDPOINT = '/contract-remediation/summary';
export const CRO_DETAIL_API_ENDPOINT = '/contract-remediation/detail';

const emptyFilters: CROFilters = {
  taxIds: [],
  geoRegion: [],
  geoState: [],
  geoMarket: [],
};

const httpService = new HttpService();
httpService.setBaseURL(
  checkIsInternal() ? config('API_DOMAIN_INTERNAL') || '' : config('API_DOMAIN_EXTERNAL') || ''
);

const readSessionObject = (key: string): Record<string, unknown> => {
  try {
    const value = JSON.parse(sessionStorage.getItem(key) || '{}');
    return value && typeof value === 'object' && !Array.isArray(value) ? value : {};
  } catch {
    return {};
  }
};

const getSelectionValues = (values: Array<{ value: string }> = []): string[] =>
  values.map((item) => item.value).filter((value) => value && value !== 'All');

const getStoredSelectionValues = (key: string): string[] => {
  try {
    const value = JSON.parse(sessionStorage.getItem(key) || '[]');
    if (!Array.isArray(value)) return [];
    return value
      .map((item) => (typeof item === 'string' ? item : item?.value))
      .filter((item): item is string => Boolean(item) && item !== 'All');
  } catch {
    return [];
  }
};

const getGeoValue = (values: Array<{ value: string }> = [], storageKey: string): string => {
  const selectedValues = getSelectionValues(values);
  const storedValues = getStoredSelectionValues(storageKey);
  return [...(selectedValues.length > 0 ? selectedValues : storedValues)].join(',');
};

const getSelectedTin = (filters: CROFilters): string => {
  const state = readSessionObject('state');
  const currentUser = (() => {
    try {
      const value = JSON.parse(sessionStorage.getItem('currentUser') || '[]');
      return Array.isArray(value) ? value[0] || {} : {};
    } catch {
      return {};
    }
  })();
  const selectedCorporateTin =
    typeof state.selectedCorporateTin === 'object' && state.selectedCorporateTin !== null
      ? (state.selectedCorporateTin as { value?: string }).value
      : undefined;
  let selectedUserTin: string | undefined;
  if (typeof currentUser.selectedTin === 'object' && currentUser.selectedTin !== null) {
    selectedUserTin = (currentUser.selectedTin as { value?: string }).value;
  } else if (typeof currentUser.selectedTin === 'string') {
    selectedUserTin = currentUser.selectedTin;
  }
  const storedTins = (sessionStorage.getItem('multiSelectedTins') || '')
    .split(',')
    .map((tin) => tin.trim())
    .filter(Boolean);

  return filters.taxIds?.[0] || selectedCorporateTin || selectedUserTin || storedTins[0] || '';
};

const getProviderSysKey = (): string => {
  const providerSysKey = readSessionObject('providerInfo').ProviderSysKey;
  return typeof providerSysKey === 'string' || typeof providerSysKey === 'number'
    ? String(providerSysKey)
    : '';
};

export const buildCROSummaryRequest = (filters: CROFilters = emptyFilters): CROSummaryRequest => {
  const isContractIdFlow = sessionStorage.getItem('contractIdNavigation') === 'true';
  const geoCriteria = sessionStorage.getItem('GeoCriteria');
  const isGeoFlow = Boolean(geoCriteria && geoCriteria !== 'null');

  if (isContractIdFlow) {
    return {
      ContractId: filters.contractIds?.[0] || getContractIdValuesFromSession()[0] || '',
    };
  }

  if (isGeoFlow) {
    return {
      Region: getGeoValue(filters.geoRegion, 'VBC_GEO_SELECTED_REGION'),
      State: getGeoValue(filters.geoState, 'VBC_GEO_SELECTED_STATES'),
      Market: getGeoValue(filters.geoMarket, 'VBC_GEO_SELECTED_MARKET'),
    };
  }

  const tins = getSelectedTin(filters);
  if (sessionStorage.getItem('multiTinNavigation')) {
    return { Tins: tins };
  }

  return { ProviderSysKey: getProviderSysKey() };
};

export const buildCRODetailRequest = (
  filters: CROFilters = emptyFilters,
  initiative: string
): CRODetailRequest => {
  const isContractIdFlow = sessionStorage.getItem('contractIdNavigation') === 'true';
  const geoCriteria = sessionStorage.getItem('GeoCriteria');
  const isGeoFlow = Boolean(geoCriteria && geoCriteria !== 'null');

  if (isContractIdFlow) {
    return {
      ContractId: filters.contractIds?.[0] || getContractIdValuesFromSession()[0] || '',
      Initiative: initiative,
    };
  }

  if (isGeoFlow) {
    return {
      Region: getGeoValue(filters.geoRegion, 'VBC_GEO_SELECTED_REGION').toUpperCase(),
      State: getGeoValue(filters.geoState, 'VBC_GEO_SELECTED_STATES').toUpperCase(),
      Market: getGeoValue(filters.geoMarket, 'VBC_GEO_SELECTED_MARKET'),
      Initiative: initiative,
    };
  }

  if (sessionStorage.getItem('multiTinNavigation')) {
    return {
      Tins: getSelectedTin(filters),
      Initiative: initiative,
    };
  }

  return {
    ProviderSysKey: getProviderSysKey(),
    Initiative: initiative,
  };
};

const isValidSummaryResponse = (response: CROSummaryApiResponse) =>
  response?.StatusCode === 200 &&
  response.Data &&
  Number.isInteger(response.Data.TotalProviderTinCount) &&
  Array.isArray(response.Data.Metrics);

const isValidDetailResponse = (response: CRODetailApiResponse) =>
  response?.StatusCode === 200 && response.Data && Array.isArray(response.Data.Metrics);

const formatSummaryMetricLabel = (label: string): string =>
  label.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/^./, (character) => character.toUpperCase());

const mapSummaryMetrics = (metric: CROSummaryApiMetric): SummaryMetric[] => {
  const initiative = metric.Initiative;
  const responseMetrics = metric.SummaryMetrics;

  if (
    !responseMetrics ||
    (initiative === 'Evaluation & Management Sloping' && metric.ProviderTinCount <= 1)
  )
    return [];

  const trendConfig = getConfiguredTrend(initiative);
  const previousValue = trendConfig ? responseMetrics[trendConfig.previousApiField] : undefined;
  const currentValue = trendConfig ? responseMetrics[trendConfig.currentApiField] : undefined;
  const trendDifference =
    typeof previousValue === 'number' && typeof currentValue === 'number'
      ? currentValue - previousValue
      : undefined;

  const summaryMetrics = Object.entries(responseMetrics)
    .filter(
      ([apiField, value]) =>
        apiField !== trendConfig?.previousApiField &&
        apiField !== trendConfig?.currentApiField &&
        (typeof value === 'number' || typeof value === 'string')
    )
    .sort(([firstApiField], [secondApiField]) => {
      const summaryMetricOrder = getSummaryMetricOrder(initiative);
      if (summaryMetricOrder.length === 0) return 0;
      const firstIndex = summaryMetricOrder.indexOf(firstApiField);
      const secondIndex = summaryMetricOrder.indexOf(secondApiField);
      if (firstIndex === -1 && secondIndex === -1) return 0;
      if (firstIndex === -1) return 1;
      if (secondIndex === -1) return -1;
      return firstIndex - secondIndex;
    })
    .map(([apiField, value]) => {
      const configuredMetric = getConfiguredMetric(initiative, apiField);
      return {
        label: configuredMetric?.label ?? formatSummaryMetricLabel(apiField),
        value: value as number | string,
        metric: configuredMetric?.metric ?? 'text',
      } as SummaryMetric;
    });

  if (!trendConfig || trendDifference === undefined) return summaryMetrics;

  let trend: SummaryMetric['trend'] = 'neutral';
  if (trendDifference > 0) trend = 'positive';
  else if (trendDifference < 0) trend = 'negative';

  return [
    ...summaryMetrics,
    {
      label: trendConfig.label,
      value: Number(trendDifference.toFixed(2)),
      metric: 'currency',
      trend,
      trendOnly: true,
    },
  ];
};

const mapSummaryRows = (response: CROSummaryApiResponse): TopMarketInitiativeRow[] =>
  response.Data.Metrics.map((metric) => ({
    initiative: metric.Initiative,
    remediationOpportunity: metric.Remediation,
    rank: metric.Rank,
    tinCount: metric.ProviderTinCount,
    summaryMetrics: mapSummaryMetrics(metric),
  }));

export const getCROSummary = async (
  filters: CROFilters = emptyFilters
): Promise<CROSummaryResult> => {
  const response = await httpService.push<CROSummaryApiResponse, CROSummaryRequest>(
    CRO_SUMMARY_API_ENDPOINT,
    buildCROSummaryRequest(filters)
  );

  if (!isValidSummaryResponse(response)) {
    throw new Error('Invalid CRO summary response');
  }

  return {
    totalProviderTinCount: response.Data.TotalProviderTinCount,
    topMarketInitiativesData: mapSummaryRows(response),
  };
};

export const getCRODetail = async (
  initiative: string,
  filters: CROFilters = emptyFilters
): Promise<CRODetailResult> => {
  const response = await httpService.push<CRODetailApiResponse, CRODetailRequest>(
    CRO_DETAIL_API_ENDPOINT,
    buildCRODetailRequest(filters, initiative)
  );

  if (!isValidDetailResponse(response)) {
    throw new Error('Invalid CRO detail response');
  }

  return { metrics: response.Data.Metrics };
};
